import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { 
  Baby,
  Heart,
  AlertTriangle,
  Clock,
  Activity,
  Download,
  RotateCcw,
  Sparkles,
  Loader2,
  Calendar,
  Droplet,
  Shield,
  User,
  Eye
} from 'lucide-react';

import { API_URL } from '../config';

export default function AccouchementAidePage() {
  const { token } = useAuth();
  const printRef = useRef(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const [formData, setFormData] = useState(() => {
    try {
      const saved = localStorage.getItem('accouchement-aide');
      return saved ? JSON.parse(saved) : {
        datePrevue: '',
        datePrevueInconnue: false,
        nbAccouchementsPrecedents: '',
        vb: '',
        cesarienne: '',
        facteurs: [],
        particularites: [],
        perteSang: null,
        perteLiquide: null,
        heureRupture: '',
        couleurLiquide: null,
        mouvementsBebe: null,
        contractions: null,
        heureDebutContractions: '',
        contractionsType: null,
        intensiteDouleur: '',
        enviePousser: null,
        preparation: {},
        presentation: null,
        bebeSort: null,
        complicationsSiege: [],
        bebeEtat: null,
        etatPlacenta: null,
        perteSangPost: null,
        hemorragieCesse: null
      };
    } catch {
      return {
        datePrevue: '',
        datePrevueInconnue: false,
        nbAccouchementsPrecedents: '',
        vb: '',
        cesarienne: '',
        facteurs: [],
        particularites: [],
        perteSang: null,
        perteLiquide: null,
        heureRupture: '',
        couleurLiquide: null,
        mouvementsBebe: null,
        contractions: null,
        heureDebutContractions: '',
        contractionsType: null,
        intensiteDouleur: '',
        enviePousser: null,
        preparation: {},
        presentation: null,
        bebeSort: null,
        complicationsSiege: [],
        bebeEtat: null,
        etatPlacenta: null,
        perteSangPost: null,
        hemorragieCesse: null
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('accouchement-aide', JSON.stringify(formData));
  }, [formData]);

  const handleReset = () => {
    if (window.confirm('Réinitialiser le formulaire ?')) {
      localStorage.removeItem('accouchement-aide');
      window.location.reload();
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Génération du PDF...');
      const opt = {
        margin: 10,
        filename: `Aide-Accouchement-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().set(opt).from(printRef.current).save();
      toast.success('PDF exporté');
    } catch (error) {
      toast.error('Erreur export PDF');
    }
  };

  const handleAnalyzeAI = async () => {
    try {
      setIsAnalyzing(true);
      toast.info('Analyse IA en cours...');

      const response = await fetch(`${API_URL}/api/ai/analyze-accouchement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Erreur analyse');
      
      const data = await response.json();
      setAiAnalysis(data);
      toast.success('Analyse terminée');
    } catch (error) {
      toast.error('Erreur analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const toggleFacteur = (facteur) => {
    setFormData(prev => ({
      ...prev,
      facteurs: prev.facteurs.includes(facteur)
        ? prev.facteurs.filter(f => f !== facteur)
        : [...prev.facteurs, facteur]
    }));
  };

  const toggleParticularite = (part) => {
    setFormData(prev => ({
      ...prev,
      particularites: prev.particularites.includes(part)
        ? prev.particularites.filter(p => p !== part)
        : [...prev.particularites, part]
    }));
  };

  const togglePreparation = (item) => {
    setFormData(prev => ({
      ...prev,
      preparation: {
        ...prev.preparation,
        [item]: !prev.preparation[item]
      }
    }));
  };

  const RadioButton = ({ label, selected, onClick, color = "bg-cyan-400" }) => (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs md:text-sm rounded-lg font-medium transition-all ${
        selected ? `${color} text-white` : 'bg-[hsl(var(--bg-elev))] text-[hsl(var(--text-dim))]'
      }`}
    >
      {label}
    </button>
  );

  const facteursRisque = [
    "Antécédent d'hémorragie de la délivrance",
    "Grossesse multiple",
    "Macrosomie fœtale",
    "Fibromes utérins"
  ];

  const particularitesGrossesse = [
    "Présentation du siège",
    "Prématurité",
    "Rupture prématurée membranes",
    "Hypertension gravidique"
  ];

  const materielMaman = [
    "1 champ de table",
    "Champ sous-fessier avec double poche",
    "2 champs d'accueil bébé absorbants",
    "Compresses",
    "2 clamps de Barr",
    "Ciseaux pour cordon",
    "Filet tubulaire",
    "Antiseptique",
    "Bonnet",
    "Délivrance dirigée préparée"
  ];

  const materielBebe = [
    "Couverture de survie",
    "Sonde aspiration CH10",
    "BAVU nouveau-né + O₂",
    "Oxymètre de pouls",
    "Électrodes pour scope",
    "Stéthoscope"
  ];

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="space-y-2 print:hidden">
        <div className="flex flex-col sm:flex-row justify-between gap-2">
          <div className="flex items-center gap-2">
            <Baby className="w-6 h-6 md:w-8 md:h-8 text-pink-400" />
            <div>
              <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--text))]">Aide à l'accouchement</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Guide pas à pas pour le médecin</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleAnalyzeAI} disabled={isAnalyzing} size="sm" className="bg-purple-400/20 text-purple-300 border-purple-400/30">
              {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </Button>
            <Button onClick={handleReset} size="sm" variant="outline" className="text-amber-300 border-amber-400/30">
              <RotateCcw className="w-4 h-4" />
            </Button>
            <Button onClick={handleExportPDF} size="sm" className="bg-cyan-400/20 text-cyan-300 border-cyan-400/30">
              <Download className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <Alert className="bg-pink-400/10 border-pink-400/30 p-2">
          <Heart className="h-4 w-4 text-pink-400" />
          <AlertDescription className="text-xs text-[hsl(var(--text))]">
            <strong>Accouchement inopiné :</strong> Guide d'aide à la décision pour les urgences
          </AlertDescription>
        </Alert>
      </div>

      <div ref={printRef} className="space-y-3">
        {/* INFORMATIONS GROSSESSE */}
        <Card className="border-pink-400/30 bg-pink-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-pink-300 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              INFORMATIONS GROSSESSE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <label className="text-xs font-medium text-[hsl(var(--text))] mb-2 block">Date prévue de l'accouchement</label>
              <Input
                type="date"
                value={formData.datePrevue}
                onChange={(e) => setFormData(prev => ({ ...prev, datePrevue: e.target.value }))}
                className="h-9 text-sm"
              />
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <Checkbox 
                  checked={formData.datePrevueInconnue}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, datePrevueInconnue: checked }))}
                />
                <span className="text-xs text-[hsl(var(--text))]">Date prévue inconnue</span>
              </label>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-[hsl(var(--text-dim))] mb-1 block">Nb accouchements</label>
                <Input
                  type="number"
                  value={formData.nbAccouchementsPrecedents}
                  onChange={(e) => setFormData(prev => ({ ...prev, nbAccouchementsPrecedents: e.target.value }))}
                  className="h-8 text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-[hsl(var(--text-dim))] mb-1 block">dont VB</label>
                <Input
                  type="number"
                  value={formData.vb}
                  onChange={(e) => setFormData(prev => ({ ...prev, vb: e.target.value }))}
                  className="h-8 text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-[hsl(var(--text-dim))] mb-1 block">Césarienne</label>
                <Input
                  type="number"
                  value={formData.cesarienne}
                  onChange={(e) => setFormData(prev => ({ ...prev, cesarienne: e.target.value }))}
                  className="h-8 text-sm"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Facteurs de risques d'hémorragie</div>
              <div className="space-y-1">
                {facteursRisque.map((facteur) => (
                  <label key={facteur} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.facteurs.includes(facteur)}
                      onCheckedChange={() => toggleFacteur(facteur)}
                    />
                    <span className="text-xs text-[hsl(var(--text))]">{facteur}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Particularités de cette grossesse</div>
              <div className="space-y-1">
                {particularitesGrossesse.map((part) => (
                  <label key={part} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.particularites.includes(part)}
                      onCheckedChange={() => toggleParticularite(part)}
                    />
                    <span className="text-xs text-[hsl(var(--text))]">{part}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* SITUATION ACTUELLE */}
        <Card className="border-red-400/30 bg-red-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-red-300 flex items-center gap-2">
              <Droplet className="w-4 h-4" />
              SITUATION ACTUELLE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Perte de sang</div>
              <div className="flex flex-wrap gap-2">
                {['Aucune', 'Simples traces', 'Hémorragie'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.perteSang === option}
                    onClick={() => setFormData(prev => ({ ...prev, perteSang: option }))}
                    color={option === 'Hémorragie' ? 'bg-red-500' : option === 'Simples traces' ? 'bg-amber-500' : 'bg-green-500'}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Perte de liquide</div>
              <div className="flex gap-2">
                {['oui', 'non'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.perteLiquide === option}
                    onClick={() => setFormData(prev => ({ ...prev, perteLiquide: option }))}
                    color={option === 'oui' ? 'bg-green-500' : 'bg-gray-500'}
                  />
                ))}
              </div>
            </div>

            {formData.perteLiquide === 'oui' && (
              <>
                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Heure de rupture de la poche des eaux</label>
                  <Input
                    type="datetime-local"
                    value={formData.heureRupture}
                    onChange={(e) => setFormData(prev => ({ ...prev, heureRupture: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>

                <div>
                  <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Couleur du liquide</div>
                  <div className="flex flex-wrap gap-2">
                    {['Clair', 'Teinté', 'Méconial'].map((option) => (
                      <RadioButton
                        key={option}
                        label={option}
                        selected={formData.couleurLiquide === option}
                        onClick={() => setFormData(prev => ({ ...prev, couleurLiquide: option }))}
                        color={option === 'Clair' ? 'bg-green-500' : option === 'Teinté' ? 'bg-amber-500' : 'bg-red-500'}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* CONTRACTIONS & TRAVAIL */}
        <Card className="border-purple-400/30 bg-purple-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              CONTRACTIONS & TRAVAIL
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Mouvements du bébé</div>
              <div className="flex gap-2">
                {['oui', 'non', 'Ne sait pas'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.mouvementsBebe === option}
                    onClick={() => setFormData(prev => ({ ...prev, mouvementsBebe: option }))}
                    color={option === 'oui' ? 'bg-green-500' : option === 'non' ? 'bg-red-500' : 'bg-gray-500'}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Contractions</div>
              <div className="flex gap-2">
                {['oui', 'non'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.contractions === option}
                    onClick={() => setFormData(prev => ({ ...prev, contractions: option }))}
                    color={option === 'oui' ? 'bg-green-500' : 'bg-gray-500'}
                  />
                ))}
              </div>
            </div>

            {formData.contractions === 'oui' && (
              <>
                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Heure de début des contractions</label>
                  <Input
                    type="datetime-local"
                    value={formData.heureDebutContractions}
                    onChange={(e) => setFormData(prev => ({ ...prev, heureDebutContractions: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>

                <div>
                  <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Les contractions semblent</div>
                  <div className="flex gap-2">
                    {['régulières', 'irrégulières'].map((option) => (
                      <RadioButton
                        key={option}
                        label={option}
                        selected={formData.contractionsType === option}
                        onClick={() => setFormData(prev => ({ ...prev, contractionsType: option }))}
                        color={option === 'régulières' ? 'bg-green-500' : 'bg-amber-500'}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-[hsl(var(--text))] mb-1 block">Intensité de la douleur sur 10</label>
                  <Input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.intensiteDouleur}
                    onChange={(e) => setFormData(prev => ({ ...prev, intensiteDouleur: e.target.value }))}
                    className="h-9 text-sm"
                    placeholder="0-10"
                  />
                </div>
              </>
            )}

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Envie irrépressible de pousser</div>
              <div className="flex gap-2">
                {['oui', 'non'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.enviePousser === option}
                    onClick={() => setFormData(prev => ({ ...prev, enviePousser: option }))}
                    color={option === 'oui' ? 'bg-red-500' : 'bg-green-500'}
                  />
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PRÉPARATION & MATÉRIEL */}
        <Card className="border-cyan-400/30 bg-cyan-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-cyan-300 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              PRÉPARATION & MATÉRIEL
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <Alert className="bg-blue-500/10 border-blue-400/30 p-2 mb-3">
              <AlertDescription className="text-xs text-blue-300">
                <strong>Installation maman :</strong><br/>
                • Fesses dépassant du bord dans le vide<br/>
                • Tête surélevée, Cuisses en hyperflexion<br/>
                • Chauffer la pièce + VSAV
              </AlertDescription>
            </Alert>

            <div>
              <div className="text-xs font-semibold text-[hsl(var(--text))] mb-2">Matériel côté maman</div>
              <div className="space-y-1">
                {materielMaman.map((item) => (
                  <label key={item} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.preparation[item] || false}
                      onCheckedChange={() => togglePreparation(item)}
                    />
                    <span className="text-xs text-[hsl(var(--text))]">{item}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold text-[hsl(var(--text))] mb-2">Matériel accueil nouveau-né</div>
              <div className="space-y-1">
                {materielBebe.map((item) => (
                  <label key={item} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox 
                      checked={formData.preparation[item] || false}
                      onCheckedChange={() => togglePreparation(item)}
                    />
                    <span className="text-xs text-[hsl(var(--text))]">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PRÉSENTATION */}
        <Card className="border-orange-400/30 bg-orange-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-orange-300 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              PRÉSENTATION
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Est-ce que vous voyez :</div>
              <div className="flex flex-col gap-2">
                {['la tête', 'les fesses ou les pieds', 'rien'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.presentation === option}
                    onClick={() => setFormData(prev => ({ ...prev, presentation: option }))}
                    color={option === 'la tête' ? 'bg-green-500' : option === 'les fesses ou les pieds' ? 'bg-red-500' : 'bg-gray-500'}
                  />
                ))}
              </div>
            </div>

            {formData.presentation === 'les fesses ou les pieds' && (
              <Alert className="bg-red-500/20 border-red-500/40 p-2">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-xs text-red-300">
                  <strong>ACCOUCHEMENT À RISQUE !</strong><br/>
                  • Appeler Centre 15 pour renfort<br/>
                  • METTEZ VOS MAINS DANS VOTRE DOS jusqu'à sortie complète<br/>
                  • Ne pas toucher le bébé (risque coincement bras/tête)<br/>
                  • Renfort SMUR pédiatrique recommandé
                </AlertDescription>
              </Alert>
            )}

            {formData.presentation === 'la tête' && (
              <div>
                <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Le bébé</div>
                <div className="space-y-1.5">
                  <RadioButton
                    label="sort normalement"
                    selected={formData.bebeSort === 'normal'}
                    onClick={() => setFormData(prev => ({ ...prev, bebeSort: 'normal' }))}
                    color="bg-green-500"
                  />
                  <RadioButton
                    label="le dos tourne en arrière"
                    selected={formData.bebeSort === 'dos_arriere'}
                    onClick={() => setFormData(prev => ({ ...prev, bebeSort: 'dos_arriere' }))}
                    color="bg-red-500"
                  />
                  <RadioButton
                    label="la tête reste bloquée (épaules visibles)"
                    selected={formData.bebeSort === 'tete_bloquee'}
                    onClick={() => setFormData(prev => ({ ...prev, bebeSort: 'tete_bloquee' }))}
                    color="bg-red-500"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* PRISE EN CHARGE NOUVEAU-NÉ */}
        <Card className="border-green-400/30 bg-green-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-green-300 flex items-center gap-2">
              <Baby className="w-4 h-4" />
              PRISE EN CHARGE NOUVEAU-NÉ
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <Alert className="bg-green-500/10 border-green-400/30 p-2">
              <AlertDescription className="text-xs text-green-300">
                <strong>Soins immédiats :</strong><br/>
                • Sécher / stimuler le bébé<br/>
                • Mettre le bonnet
              </AlertDescription>
            </Alert>

            <div className="p-3 rounded-lg bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
              <div className="text-xs font-semibold text-[hsl(var(--text))] mb-3">CLAMP DU CORDON (1-3 min de vie)</div>
              <div className="space-y-2 text-xs text-[hsl(var(--text-dim))]">
                <div>1. Clamper dès que le cordon ne bat plus / devient blanc</div>
                <div>2. Placer 1er clamp à 10-15 cm de l'ombilic</div>
                <div>3. Comprimer le cordon et glisser pour le vider</div>
                <div>4. Placer 2ème clamp à 3-5 cm du 1er</div>
                <div>5. Sectionner entre les deux clamps</div>
                <div>6. Recouvrir avec compresse stérile + filet tubulaire</div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">État du bébé</div>
              <div className="space-y-2">
                <RadioButton
                  label="Cri tonique, respiration sans difficulté"
                  selected={formData.bebeEtat === 'normal'}
                  onClick={() => setFormData(prev => ({ ...prev, bebeEtat: 'normal' }))}
                  color="bg-green-500"
                />
                <RadioButton
                  label="Pas de cri, tonus faible, difficultés respiratoires"
                  selected={formData.bebeEtat === 'detresse'}
                  onClick={() => setFormData(prev => ({ ...prev, bebeEtat: 'detresse' }))}
                  color="bg-red-500"
                />
              </div>
            </div>

            <Alert className="bg-blue-500/10 border-blue-400/30 p-2">
              <AlertDescription className="text-xs text-blue-300">
                <strong>Peau-à-peau :</strong> Mettre contre maman, tête dégagée, protéger du froid (couverture survie)
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* DÉLIVRANCE & HÉMORRAGIE */}
        <Card className="border-orange-400/30 bg-orange-400/5">
          <CardHeader className="p-3">
            <CardTitle className="text-sm md:text-base text-orange-300 flex items-center gap-2">
              <Droplet className="w-4 h-4" />
              DÉLIVRANCE & HÉMORRAGIE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3">
            <Alert className="bg-amber-500/20 border-amber-500/40 p-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <AlertDescription className="text-xs text-amber-300">
                <strong>Délivrance dirigée :</strong><br/>
                • 5 UI Oxytocine (Syntocinon®) dans 5mL NaCl 0,9% en IVD lente<br/>
                • Dans les 5 min après expulsion<br/>
                • Délai max 30 min après expulsion
              </AlertDescription>
            </Alert>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Le placenta</div>
              <div className="flex flex-col gap-2">
                {['est sorti dans sa totalité', 'est sorti partiellement', 'n\'est pas sorti'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.etatPlacenta === option}
                    onClick={() => setFormData(prev => ({ ...prev, etatPlacenta: option }))}
                    color={
                      option === 'est sorti dans sa totalité' ? 'bg-green-500' : 
                      option === 'est sorti partiellement' ? 'bg-amber-500' : 
                      'bg-red-500'
                    }
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-[hsl(var(--text))] mb-2">Perte de sang évaluée</div>
              <div className="flex flex-col gap-2">
                {['< 500 ml', '≥ 500 ml'].map((option) => (
                  <RadioButton
                    key={option}
                    label={option}
                    selected={formData.perteSangPost === option}
                    onClick={() => setFormData(prev => ({ ...prev, perteSangPost: option }))}
                    color={option === '< 500 ml' ? 'bg-green-500' : 'bg-red-500'}
                  />
                ))}
              </div>
            </div>

            {formData.perteSangPost === '≥ 500 ml' && (
              <Alert className="bg-red-500/20 border-red-500/40 p-3">
                <AlertTriangle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-xs text-red-300">
                  <strong>PROTOCOLE HÉMORRAGIE SÉVÈRE :</strong><br/><br/>
                  
                  <strong>1. MASSER FERMEMENT</strong> le ventre sous le nombril sans interruption<br/>
                  <strong>2. NOTER l'heure de début</strong><br/>
                  <strong>3. Monitorage :</strong> TA, pouls, SpO₂<br/>
                  <strong>4. VVP + Hémocue</strong><br/>
                  <strong>5. Lutter contre l'hypothermie</strong><br/>
                  <strong>6. SONDAGE vésical à demeure</strong><br/>
                  <strong>7. REMPLISSAGE Isofundine 500mL sur 10 min</strong><br/><br/>
                  
                  <strong>Si délivrance réalisée :</strong><br/>
                  • 5 UI Oxytocine IVD lente<br/>
                  • 10 UI Oxytocine dans 500mL G5% (débit 250 mL/h)<br/>
                  • 1g Acide Tranexamique dans 100mL NaCl sur 10 min<br/><br/>
                  
                  <strong>Si hémorragie persiste :</strong><br/>
                  • Poser 2ème VVP<br/>
                  • REMPLISSAGE 500mL sur 10 min<br/>
                  • Renouveler Acide Tranexamique 1g<br/>
                  • Continuer massage utérin<br/>
                  • TRANSPORT RAPIDE avec Centre 15
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* AI ANALYSIS */}
        {aiAnalysis && (
          <Card className="border-purple-500/30 bg-purple-500/5">
            <CardHeader className="p-3">
              <CardTitle className="text-sm md:text-base text-purple-300 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                ANALYSE IA - Recommandations Obstétricales
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap text-xs md:text-sm text-[hsl(var(--text))]">
                  {aiAnalysis.analysis}
                </div>
              </div>
              <Alert className="mt-3 bg-amber-400/10 border-amber-400/30 p-2">
                <AlertDescription className="text-xs text-amber-300">
                  <strong>Disclaimer :</strong> Aide à la décision, consultation obstétricale requise
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
